import { PublicClientApplication } from '@azure/msal-browser';
import type {
  AccountInfo,
  AuthenticationResult,
  EndSessionRequest,
  RedirectRequest,
  PopupRequest,
  SilentRequest,
} from '@azure/msal-browser';
import { msalConfig, loginRequest, apiRequest } from '@/config/msalConfig';

class AuthService {
  private msalInstance: PublicClientApplication;
  private account: AccountInfo | null = null;

  constructor() {
    this.msalInstance = new PublicClientApplication(msalConfig);
  }

  /**
   * Initialize MSAL instance
   * Call this before using any other methods
   */
  async initialize(): Promise<void> {
    await this.msalInstance.initialize();
    await this.handleRedirectPromise();
  }

  /**
   * Handle redirect promise after login
   */
  private async handleRedirectPromise(): Promise<void> {
    try {
      const response = await this.msalInstance.handleRedirectPromise();
      if (response) {
        this.account = response.account;
      } else {
        // Check if there's an account in cache
        const accounts = this.msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          this.account = accounts[0];
        }
      }
    } catch (error) {
      console.error('Error handling redirect:', error);
    }
  }

  /**
   * Login with popup
   */
  async loginPopup(): Promise<AuthenticationResult> {
    const request: PopupRequest = {
      ...loginRequest,
      prompt: 'select_account',
    };

    try {
      const response = await this.msalInstance.loginPopup(request);
      this.account = response.account;
      return response;
    } catch (error) {
      console.error('Login popup failed:', error);
      throw error;
    }
  }

  /**
   * Login with redirect
   */
  async loginRedirect(): Promise<void> {
    const request: RedirectRequest = {
      ...loginRequest,
      prompt: 'select_account',
    };

    try {
      await this.msalInstance.loginRedirect(request);
    } catch (error) {
      console.error('Login redirect failed:', error);
      throw error;
    }
  }

  /**
   * Logout
   */
  async logout(): Promise<void> {
    const logoutRequest: EndSessionRequest = {
      account: this.account || undefined,
      postLogoutRedirectUri: msalConfig.auth.postLogoutRedirectUri,
    };

    try {
      await this.msalInstance.logoutRedirect(logoutRequest);
      this.account = null;
    } catch (error) {
      console.error('Logout failed:', error);
      throw error;
    }
  }

  /**
   * Get access token silently
   * Defaults to API scopes for backend API access
   */
  async getAccessToken(scopes?: string[]): Promise<string> {
    const account = this.getAccount();
    if (!account) {
      throw new Error('No active account! Please login first.');
    }

    const request: SilentRequest = {
      // Use apiRequest scopes by default (for backend API), or custom scopes if provided
      scopes: scopes || apiRequest.scopes,
      account: account,
    };

    try {
      const response = await this.msalInstance.acquireTokenSilent(request);
      console.log('[AuthService] Access token acquired for scopes:', request.scopes);
      return response.accessToken;
    } catch (error) {
      console.warn('[AuthService] Silent token acquisition failed, trying interactive:', error);
      // If silent acquisition fails, try popup
      const response = await this.msalInstance.acquireTokenPopup(request);
      console.log('[AuthService] Access token acquired via popup for scopes:', request.scopes);
      return response.accessToken;
    }
  }

  /**
   * Get the current account
   */
  getAccount(): AccountInfo | null {
    if (this.account) {
      return this.account;
    }

    const accounts = this.msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      this.account = accounts[0];
      return this.account;
    }

    return null;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.getAccount() !== null;
  }

  /**
   * Get user info
   */
  getUserInfo(): { name?: string; email?: string; username?: string } | null {
    const account = this.getAccount();
    if (!account) {
      return null;
    }

    return {
      name: account.name,
      email: account.username,
      username: account.username,
    };
  }

  /**
   * Get MSAL instance
   */
  getInstance(): PublicClientApplication {
    return this.msalInstance;
  }
}

// Export singleton instance
export const authService = new AuthService();
